import type { IconName } from '@w7/ui'
import type { KcMfaAction } from '@/auth/oidc'

export type MethodStatus = 'ready' | 'soon' | 'blocked'

export interface MfaMethod {
  id: string
  icon: IconName
  title: string
  body: string
  status: MethodStatus
  statusLabel: string
  actionLabel?: string
  /** Keycloak AIA — undefined for deferred methods. */
  kcAction?: KcMfaAction
  /** Short W7 wizard steps before the IdP ceremony. */
  steps?: string[]
}

/** Catalog for /settings/security — enroll via branded wizards + Keycloak AIA. */
export const MFA_METHODS: MfaMethod[] = [
  {
    id: 'totp',
    icon: 'lock',
    title: 'Authenticator app',
    body: 'A rotating one-time code from Google Authenticator, Authy, 1Password, or Microsoft Authenticator — entered after your password.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Set up authenticator',
    kcAction: 'CONFIGURE_TOTP',
    steps: [
      'Open your authenticator app on your phone.',
      'Continue — you will scan a QR code (or enter a secret) on the secure enroll step.',
      'Confirm with a 6-digit code. Next login will ask for that code after your password.',
    ],
  },
  {
    id: 'passkey',
    icon: 'shield',
    title: 'This device (passkey)',
    body: 'Approve sign-in with Face ID, Touch ID, Windows Hello, fingerprint, or a security key — Keycloak stores one passkey credential for all of these.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Register this device',
    kcAction: 'webauthn-register',
    steps: [
      'Use this phone or laptop as a passkey (biometrics, PIN, or security key).',
      'Continue — your browser will ask to create a passkey.',
      'Your fingerprint or face never leaves the device; only a signed approval is stored.',
    ],
  },
  {
    id: 'fingerprint',
    icon: 'user',
    title: 'Fingerprint',
    body: 'Shortcut into the same passkey enroll — not a separate Keycloak method. If you already registered a passkey, this card stays “Covered”, not a second Active.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Register fingerprint',
    kcAction: 'webauthn-register',
    steps: [
      'Make sure this device has a fingerprint sensor enrolled in the OS.',
      'Continue — choose this device / fingerprint when the browser prompts.',
      'This creates the same WebAuthn credential as “This device (passkey)”.',
    ],
  },
  {
    id: 'security-key',
    icon: 'lock',
    title: 'Security key',
    body: 'Shortcut into the same passkey enroll with a USB/NFC key — same Keycloak store as “This device (passkey)”.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Add a security key',
    kcAction: 'webauthn-register',
    steps: [
      'Have your USB or NFC security key ready.',
      'Continue — when prompted, choose security key and tap/insert it.',
      'This is still one WebAuthn credential in Keycloak, not a separate Active factor.',
    ],
  },
  {
    id: 'recovery',
    icon: 'copy',
    title: 'Recovery codes',
    body: 'One-time backup codes if you lose your phone or key. Store them offline.',
    status: 'ready',
    statusLabel: 'Available',
    actionLabel: 'Generate codes',
    kcAction: 'CONFIGURE_RECOVERY_AUTHN_CODES',
    steps: [
      'Continue to generate one-time recovery codes.',
      'Copy or print them and store offline — each code works once.',
      'Anyone with a code can complete sign-in once; treat them like passwords.',
    ],
  },
  {
    id: 'email',
    icon: 'info',
    title: 'Email one-time code',
    body: 'A code sent to your account email after password. Needs Keycloak SMTP before it can go live.',
    status: 'soon',
    statusLabel: 'Needs SMTP',
  },
  {
    id: 'sms',
    icon: 'live',
    title: 'SMS one-time code',
    body: 'A code texted to your phone. Needs an owner-approved SMS provider (paid) — not enabled yet.',
    status: 'blocked',
    statusLabel: 'Needs approval',
  },
]

export function getMfaMethod(id: string | undefined): MfaMethod | undefined {
  if (!id) return undefined
  return MFA_METHODS.find((m) => m.id === id)
}
